"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

export interface CareersDataProps {
  id?: string;
  healthcareProviders?: number;
  openPositions?: number;
  availableRoles?: string[];
  className?: string;
  animationDuration?: number;
}

const CareersData: React.FC<CareersDataProps> = ({
  id = "careers-data",
  healthcareProviders = 400,
  openPositions = 20,
  availableRoles = [
    "Home Care Aide",
    "Licensed Vocational Nurse",
    "Registered Nurse",
    "Office Employee",
    "Certified Nursing Assistant",
  ],
  className,
  animationDuration = 3000,
}) => {
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);

  useEffect(() => {
    // Skip animation if there's only one role
    if (availableRoles.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentRoleIndex(
        (prevIndex) => (prevIndex + 1) % availableRoles.length
      );
    }, animationDuration);

    return () => clearInterval(interval);
  }, [availableRoles.length, animationDuration]);

  return (
    <section
      id={id}
      aria-label="Job Status Statistics"
      className={cn("w-full bg-primary-100", className)}
    >
      <div className="flex flex-col gap-6 p-4 md:gap-8 md:p-6 lg:mx-auto lg:max-w-lg lg:gap-10 lg:px-3">
        <div className="flex flex-wrap justify-center gap-6 md:gap-16">
          <div className="flex items-start gap-4 self-stretch md:gap-10 lg:gap-16">
            <div className="flex min-w-max flex-col items-center sm:min-w-[171px] md:min-w-fit">
              <div className="font-montserrat text-heading-4 font-medium leading-tight text-primary-500 md:text-heading-2">
                {healthcareProviders}+
              </div>
              <div className="font-roboto text-body-1-300 font-light text-neutral-500">
                healthcare providers
              </div>
            </div>
            <div className="flex min-w-max flex-col items-center sm:min-w-[171px] md:min-w-fit">
              <div className="font-montserrat text-heading-4 font-medium leading-tight text-primary-500 md:text-heading-2">
                {openPositions}+
              </div>
              <div className="font-roboto text-body-1-300 font-light text-neutral-500">
                open positions
              </div>
            </div>
          </div>
          <div className="flex min-w-full flex-col justify-center self-stretch text-center sm:min-w-[322px]">
            <div className="font-roboto text-body-1-300 font-light text-neutral-500">
              AVAILABLE ROLES
            </div>
            <div className="relative mt-1 flex min-h-8 w-full flex-col justify-center self-center overflow-hidden font-montserrat text-subheading-3-500 leading-none text-neutral-600 md:text-subheading-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentRoleIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    alignSelf: "stretch",
                    width: "100%",
                    position: "absolute",
                    left: 0,
                    right: 0,
                  }}
                >
                  {availableRoles[currentRoleIndex]}
                </motion.div>
              </AnimatePresence>
              {/* Invisible element to maintain height */}
              <div className="invisible w-full self-stretch">
                {availableRoles[0]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareersData;
